import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Quote, Stethoscope, Shield, CircleDot, Briefcase } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const ACCENTS = ["#FF2D5C", "#FF3DCB", "#9B4DFF", "#3DBFFF"];

// Subtle role-based icon + colour (kept neutral and editorial — never corporate)
const ROLE_META = {
  clinician:  { Icon: Stethoscope, dot: "#3DBFFF" },
  leadership: { Icon: Shield,      dot: "#FF2D5C" },
  player:     { Icon: CircleDot,   dot: "#FF3DCB" },
  manager:    { Icon: Briefcase,   dot: "#9B4DFF" },
};

const Udtalelser = () => {
  const { t } = useTranslation();
  const items = t("udtalelser.items", { returnObjects: true });

  return (
    <section
      id="udtalelser"
      data-testid="udtalelser-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <span className="eyebrow">{t("udtalelser.eyebrow")}</span>
          <h2 className="font-display mt-5 text-xl sm:text-[1.5rem] lg:text-[1.75rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
            {t("udtalelser.title")}
          </h2>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(items) &&
            items.map((it, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              const meta = ROLE_META[it.category] || ROLE_META.player;
              const Icon = meta.Icon;
              return (
                <motion.figure
                  key={i}
                  data-testid={`udtalelser-item-${i}`}
                  data-category={it.category || "player"}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                  className="group relative flex flex-col overflow-hidden rounded-2xl glass p-7 electric-border"
                >
                  <Quote
                    className="absolute right-6 top-6 h-8 w-8 opacity-30 transition-opacity group-hover:opacity-70"
                    style={{ color: accent }}
                  />

                  {/* Role tag — subtle, single-line, colour-coded */}
                  {it.role && (
                    <div className="relative inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/[0.025] px-2.5 py-1 mb-5">
                      <Icon className="h-3 w-3" style={{ color: meta.dot }} />
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.22em]"
                        style={{ color: meta.dot }}
                      >
                        {it.role}
                      </span>
                    </div>
                  )}

                  <blockquote className="relative font-display text-base md:text-lg leading-relaxed text-white/90">
                    &ldquo;{it.quote}&rdquo;
                  </blockquote>

                  <figcaption className="mt-5 flex items-center gap-3 border-t border-white/5 pt-4">
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full font-display text-sm font-bold text-white"
                      style={{
                        background: `linear-gradient(135deg, ${accent}, transparent)`,
                        boxShadow: `0 0 18px -4px ${accent}`,
                      }}
                    >
                      {it.name?.[0]}
                    </span>
                    <div>
                      <div className="font-display text-sm font-semibold text-white">
                        {it.name}
                      </div>
                      {it.role && (
                        <div
                          className="font-mono text-[10px] uppercase tracking-[0.2em]"
                          style={{ color: meta.dot, opacity: 0.85 }}
                        >
                          {it.role}
                        </div>
                      )}
                    </div>
                  </figcaption>
                </motion.figure>
              );
            })}
        </div>

        <p className="mt-10 text-center font-mono text-xs uppercase tracking-[0.22em] text-white/45">
          *{t("udtalelser.source")}
        </p>
      </div>
    </section>
  );
};

export default Udtalelser;
